/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import MsDyn365, { IGridSettings, IImageSettings } from '@msdyn365-commerce/core';
import { TextValueTranslation } from '@msdyn365-commerce/retail-proxy';
import { addThrottledEvent, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { computed, observable } from 'mobx';
import * as React from 'react';

import { IMenuItemData, ICittaCustomNavigationMenuData } from './citta-custom-navigation-menu.data';
import { ICittaCustomNavigationMenuProps } from './citta-custom-navigation-menu.props.autogenerated';
import { ICmsNavItemsData } from './citta-custom-navigation-menu.props.autogenerated';
import { Link } from './components/link';


export interface INavigationMenuViewProps extends ICittaCustomNavigationMenuProps<ICittaCustomNavigationMenuData> {
    className: string;
    menuItemData: IMenuItemData[];
    Navigation: IModuleProps;
    MenuList: INodeProps;
    ListItem: INodeProps;
    Link: INodeProps;
    Button: INodeProps;
    Span: INodeProps;
    ImageContainer: INodeProps;
    MobileDescriptionContainer: INodeProps;
    MobileBackButton: INodeProps;
    MobileDescriptionLabel: INodeProps;
    imageSettings: IImageSettings;
    gridSettings: IGridSettings;
    isMobileView: boolean;
    DivContainer?: INodeProps;
    ImageDivContainer?: INodeProps;
    showCategoryImage?: boolean;
    showPromotionalContent?: boolean;
    links?: React.ReactNode;

}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface INavigationMenuState {
    isMobile: boolean;
}

/**
 *
 * NavigationMenu component.
 * @extends {React.PureComponent<ICittaCustomNavigationMenuProps<ICittaCustomNavigationMenuData>>}
 */
class NavigationMenu extends React.PureComponent<ICittaCustomNavigationMenuProps<ICittaCustomNavigationMenuData>, INavigationMenuState> {

    private resizeThrottledEventHandler?: (event: Event) => void;

    @observable
    private viewport: GridSize;

    public static getFriendlyName(locale: string, nameTranslations?: TextValueTranslation[]): string | undefined {
        let nameTranslation: TextValueTranslation | undefined;
        if (locale && nameTranslations && nameTranslations.length > 0) {
            nameTranslation = nameTranslations.find(item => item.Language!.toLowerCase() === locale.toLowerCase());
        }

        return nameTranslation && nameTranslation.Text;
    }

    constructor(props: ICittaCustomNavigationMenuProps<ICittaCustomNavigationMenuData>) {
        super(props);
        this.viewport = props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? 'xs' : 'lg';
        this.state = { 
            isMobile: this.isMobile 
        };
        this._updateViewport = this._updateViewport.bind(this);
        this._getViewport = this._getViewport.bind(this);
    }

    @computed get isMobile(): boolean {
        return this.viewport === 'xs' || this.viewport === 'sm';
    }

    public componentDidMount(): void {
        if (MsDyn365.isBrowser) {
            this.resizeThrottledEventHandler = addThrottledEvent(window, 'resize', this._updateViewport as EventListener);
        }
        this._updateViewport();

        let container = document.querySelector(".ms-nav-custom");
        if (container) {
            const middle =  container.querySelector("a.active") || container.children[Math.floor((container.children.length - 1) / 2)];
           
            container.scrollTo({
                top: 0,
                 // @ts-ignore
                left:  middle.offsetLeft + middle.offsetWidth / 2 - container.offsetWidth / 2,
                behavior: 'smooth'
              })
        }
    }

    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('resize', this.resizeThrottledEventHandler!, false);
        }
    }

    public render(): JSX.Element | null {
        const { cmsNavItems } = this.props.config;

        if (!cmsNavItems || !cmsNavItems.length) {
            return null;
        }
        const viewProps = {
            ...this.props,
            ...this.state,
            Navigation: {
                moduleProps: this.props,
                className: classnames('ms-nav-custom', this.props.config.className, this.state.isMobile ? 'mobile-vp' : 'desktop-vp'),
                tag: 'nav'
            },
            isMobileView: this.state.isMobile,
            className: classnames('ms-nav', this.props.config.className),
            links: this._renderLinks()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _renderLinks(): React.ReactNode | null {
        const url = this.props.context.request.canonicalUrl!;
        const { cmsNavItems } = this.props.config;
        let currentNavItems;

        if (url) {
            const lastPartOfURL = url && url.substring(url.lastIndexOf('/') + 1)
            cmsNavItems!.forEach(navMenu => {
                let urls = navMenu.linkText!.split('|');
                if (urls.find(u => u === lastPartOfURL)) {
                    currentNavItems = this._createLinks(navMenu, lastPartOfURL);
                }
            });
        }

        return currentNavItems;
    }

    private _createLinks(navMenu: ICmsNavItemsData, lastPartOfURL: string): React.ReactNode {
        let links = navMenu.subMenus?.map(menuItem => {
        return <Link
                isActive={lastPartOfURL === menuItem.ariaLabel}
                text={menuItem.linkText}
                ariaLabel={menuItem.linkText}
                href={menuItem.linkUrl.destinationUrl}
                index='1'
                className='ms-category_link'
                telemetryContent={{pageName: `'Custom': ${menuItem.linkText}` , moduleName: 'citta custom nav menu'}}
            />
        })

        return links;
    }

    public _getViewport(): GridSize {
        const { context } = this.props;

        // Always render in mobile viewport on a mobile device
        if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'xs';
    }

    private _updateViewport(): void {
        this.viewport = this._getViewport();
        this.setState({ isMobile: this.isMobile });
    }

}

export default NavigationMenu;
